import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp } from 'lucide-react';
import { Header } from "@/components/header";
import { Footer } from '@/components/footer';
import { CTA } from '@/components/cta';
import { FAQ } from '@/components/faq';

export default function ScalabilityBenefit() {
  const { t } = useTranslation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header />
      <div className="container mx-auto px-4 py-16">        
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-slate-100 dark:bg-slate-900/30 w-16 h-16 rounded-2xl flex items-center justify-center text-slate-600 dark:text-slate-400">
              <ArrowUp className="w-8 h-8" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              {t('benefitsCloud.benefits.0.title')}
            </h1>
          </div>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-xl text-slate-600 dark:text-slate-300 mb-8">
              {t('benefitsCloud.benefits.0.description')}
            </p>
            
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
                {t('benefitsCloud.details.scalability.title', 'Scale with Your Needs')}
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
                {t('benefitsCloud.details.scalability.description', 'Our cloud infrastructure is designed to scale seamlessly with your organization, whether you\'re a small business or a large enterprise.')}
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.scalability.benefit1', 'Elastic Resources')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.scalability.benefit1Desc', 'Automatically scale up or down based on demand without manual intervention.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.scalability.benefit2', 'High Availability')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.scalability.benefit2Desc', 'Maintain performance and uptime during traffic spikes.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.scalability.benefit3', 'Distributed Architecture')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.scalability.benefit3Desc', 'Resilient infrastructure ensures reliable performance across regions.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.scalability.benefit4', 'Predictable Costs')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.scalability.benefit4Desc', 'Scale with transparent pricing and usage-based billing.')}
                  </p>
                </div>
              </div>
              
              <div className="mt-16">
                <FAQ 
                    faqs={t('benefitsCloud.details.scalability.faq', { returnObjects: true }) as Array<{id?: string, question: string, answer: string}>} 
                    title={t('faq.title', 'Frequently Asked Questions')}
                    subtitle={t('benefitsCloud.details.scalability.faqSubtitle', 'Everything you need to know about scalability')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <CTA />
      <Footer />
    </div>
  );
}
